import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";
import BasicButton from "@/src/components/atom/Button/BasicButton";
import Typo from "@/src/components/atom/Typo";
import PublicPageHeader from "@/src/components/organism/PublicPageHeader";
import PublicPageFooter from "@/src/components/molecule/PublicPageFooter";
import colorSet from "@/src/styles/color";
import IllustPerson from "@/src/assets/icons/illust-person.svg";
import PaperAirplane from "@/src/assets/icons/icon-paper-airplane.svg";
import PUBLIC_PATH from "@/src/routes/public/path";
import EXPORTER_PRIVATE_PATH from "@/src/routes/exporter/path";
import { useAppDispatch, useAppSelector } from "@/src/store";
import { useSignOutMutation } from "@/src/store/apis/auth";
import { apiSlice } from "@/src/store/apis/baseQuery";
import { currencyApi } from "@/src/store/apis/currency";
import { resetTimer } from "@/src/store/slice/sessionTime";
import { persistor } from "@/src/index";
import { useNormalizedUser } from "@/src/hooks/useNormalizedUser";

const MemberSignupResult: React.FC = () => {
  const { t } = useTranslation(["signup", "common"]);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const normalizedUser = useNormalizedUser(user);
  const [signOut] = useSignOutMutation();

  const isApproved = normalizedUser?.isExporterUser &&
    normalizedUser.originalUser &&
    "account" in normalizedUser.originalUser &&
    normalizedUser.originalUser.account.isActivated;

  const logout = async () => {
    try {
      await signOut().unwrap();
    } catch (e) {
      // Error handling - continue with logout anyway
    } finally {
      persistor.purge();
      dispatch(currencyApi.util.resetApiState());
      dispatch(apiSlice.util.resetApiState());
      dispatch(resetTimer());
      navigate(PUBLIC_PATH.LOGIN, { replace: true });
    }
  };

  const handleButtonClick = () => {
    navigate(EXPORTER_PRIVATE_PATH.HOME);
  };

  return (
    <Layout>
      <PublicPageHeader />

      <MainSection>
        <TitleSection>
          <Typo as="h1" typoType="d3" color="gray1">
            {isApproved
              ? t("signup:v2.registeredSuccessfullyTitle")
              : t("signup:v2.registrationSubmitted")}
          </Typo>
        </TitleSection>

        <SuccessCard>
          {isApproved ? (
            <IconContainer>
              <StaffIcon src={IllustPerson} alt="Staff" />
              <StatusText>
                <Typo typoType="b9m" color="gray2">
                  {t("signup:v2.registeredSuccessfully")}
                </Typo>
              </StatusText>
            </IconContainer>
          ) : (
            <ProcessContainer>
              <ProcessStep>
                <StaffIcon src={PaperAirplane} alt="Paper" />
                <ProcessInfo>
                  <ProgressBar>
                    <ProgressLeft $completed={false} />
                    <StepNumber $active={true}>
                      <Typo typoType="h10" color="white">
                        1
                      </Typo>
                    </StepNumber>
                    <ProgressRight $completed={false} />
                  </ProgressBar>
                  <Typo typoType="b9m" color="gray2">
                    {t("signup:v2.requestedForApproval")}
                  </Typo>
                </ProcessInfo>
              </ProcessStep>

              <ProcessStep>
                <StaffIcon src={IllustPerson} alt="Person" />
                <ProcessInfo>
                  <ProgressBar>
                    <ProgressLeft $completed={false} />
                    <StepNumber $active={false}>
                      <Typo typoType="b10m" color="gray8">
                        2
                      </Typo>
                    </StepNumber>
                    <ProgressRight $completed={false} />
                  </ProgressBar>
                  <Typo typoType="b9m" color="gray7">
                    {t("signup:v2.approvalCompleted")}
                  </Typo>
                </ProcessInfo>
              </ProcessStep>
            </ProcessContainer>
          )}

          <ContentSection>
            <Typo typoType="h2" color="gray1">
              {isApproved
                ? t("signup:v2.welcomeToEcoyaExport")
                : t("signup:v2.almostThere")}
            </Typo>
            <DescriptionText>
              {isApproved ? (
                <>
                  <Typo typoType="b7m" color="gray2">
                    {t("signup:v2.accountCreatedSuccessfully")}
                  </Typo>
                  <Typo typoType="b7m" color="gray2">
                    {t("signup:v2.readyToManageDocuments")}
                  </Typo>
                  <Typo typoType="b7m" color="gray2">
                    {t("signup:v2.clickToGetStarted")}
                  </Typo>
                </>
              ) : (
                <>
                  <Typo typoType="b7m" color="gray2">
                    {t("signup:v2.approvalPendingDescription1")}
                  </Typo>
                  <Typo typoType="b7m" color="gray2">
                    {t("signup:v2.approvalPendingDescription2")}
                  </Typo>
                </>
              )}
            </DescriptionText>
          </ContentSection>

          <BasicButton
            buttonGrade="primary"
            buttonColor="blue"
            buttonSize={40}
            onClick={handleButtonClick}
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              background: colorSet.blue10,
              color: colorSet.indigo,
              boxShadow:
                "0px 0px 0px 0.5px rgba(23, 98, 195, 0.2), 0px 1px 2px 0px rgba(5, 29, 57, 0.1)",
            }}
          >
            <Typo typoType="btn3m" color="indigo">
              {t("signup:v2.goToDashboard")}
            </Typo>
          </BasicButton>
        </SuccessCard>
      </MainSection>

      <PublicPageFooter />
    </Layout>
  );
};

export default MemberSignupResult;

const Layout = styled.main`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
  background: ${colorSet.white};
`;

const MainSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  width: 100%;
  padding: 60px 40px;
  gap: 40px;
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 24px 0;

  h1 {
    text-align: center;
  }
`;

const SuccessCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 720px;
  padding: 40px 48px;
  gap: 40px;
  background: ${colorSet.white};
  border-radius: 16px;
  box-shadow:
    0px 0px 0px 1px rgba(0, 0, 0, 0.1),
    0px 4px 16px 0px rgba(0, 0, 0, 0.1);
  overflow-x: clip;
  overflow-y: auto;
`;

const IconContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const StaffIcon = styled.img`
  width: 56px;
  height: 56px;
`;

const StatusText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 200px;
  gap: 8px;
  text-align: center;
`;

const ProcessContainer = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 0;
  width: 100%;
`;

const ProcessStep = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const ProcessInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 200px;
`;

const ProgressBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
`;

const ProgressLeft = styled.div<{ $completed: boolean }>`
  flex: 1;
  height: 4px;
  background: ${(props) => (props.$completed ? colorSet.gray9 : colorSet.gray11)};
  border-radius: ${(props) => (props.$completed ? "0 16px 16px 0" : "0")};
`;

const ProgressRight = styled.div<{ $completed: boolean }>`
  flex: 1;
  height: 4px;
  background: ${(props) => (props.$completed ? colorSet.gray9 : colorSet.gray11)};
  border-radius: ${(props) => (props.$completed ? "16px 0 0 16px" : "0")};
`;

const StepNumber = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 64px;
  background: ${(props) => (props.$active ? colorSet.systemBlue1 : colorSet.white)};
  border: ${(props) =>
    props.$active ? `1px solid ${colorSet.systemBlue2}` : `0.5px solid ${colorSet.gray8}`};
  padding: 2px 6px;
`;

const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
  text-align: center;
`;

const DescriptionText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;

  p {
    line-height: 24px;
  }
`;
